import { Dispatch, SetStateAction, useState } from "react"

// ** Third Party Imports
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation } from "@tanstack/react-query"

// ** Custom Hooks Imports
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Types
import { DictType } from "@pma/types"

// ** Stores
import { useAuth } from "@pma/stores/auth-store"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { EDIT_USER_ORG_PARAMETER_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"

interface NameEditorProps {
  userId: string
  refetch: CallableFunction
  name: string | null | undefined
  setEditMode: Dispatch<SetStateAction<boolean>>
}

const NameEditor = ({ userId, name, refetch, setEditMode }: NameEditorProps) => {
  const { authConfig } = useAuth()
  const { translate } = useTranslator()

  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [inputValue, setInputValue] = useState(name || "")

  const updateUserDetails = async () => {
    const response = await axios.post(
      SERVER_URL + EDIT_USER_ORG_PARAMETER_ENDPOINT,
      {
        user_id: userId,
        user_name: inputValue,
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const updateUserNameMutation = useMutation({
    mutationKey: [`roles`],
    mutationFn: () => updateUserDetails(),
    onSuccess: (data) => {
      refetch && refetch()
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data.message ||
          translate(`Error occurred while updating`) + " " + translate(`role`),
        variant: "error",
      })
    },
  })

  const onSubmit = () => {
    updateUserNameMutation.mutate()
    setEditMode(false)
  }

  return (
    <div className='flex flex-col'>
      <input
        className='rounded border bg-white/20 px-2 outline-none focus:border-primary'
        value={inputValue}
        onChange={(e) => setInputValue(e?.target?.value)}
        autoFocus
        onBlur={() => onSubmit()}
        onKeyDown={(e) => {
          if (e.key == "Enter") onSubmit()
        }}
      />
    </div>
  )
}

export default NameEditor
